import {useState, useEffect, useRef} from 'react';
import {Box, Menu, MenuItem, Tab, Tabs, Typography} from '@mui/material';
import LocaleStrings from '../../locales/en.json';
import {BulkUserResultTableColumns} from './BulKUserResulltTableColumns';
import {AurDataGrid} from '../../components/common/aur-data-grid/AurDataGrid';
import {AurDismissBanner} from '../common/aur-banner/AurDismissBanner';
import AurSvgIcon from '../common/aur-svg-icon/AurSvgIcon';
import AurButton from '../common/aur-button/AurButton';
import {t} from 'i18next';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {UserRecordsFilter} from '../pages/user-management/models';
import {
  setBulkUserEdit,
  setCurrentRow,
  setDismissbanner,
  setDrawer,
  setErrors,
  setShowToastIn,
  setSummary,
  setValue,
  setTab,
  setFixErrorTab,
} from '../../redux/slices/bulkUserEditSlice';
import {GridRowSelectionModel, GridSortModel} from '@mui/x-data-grid';
import {
  setTriggerUserDataAPI,
  setUsers,
} from '../../redux/slices/bulkUserResultSlice';
import {clearInitialUploadPage} from '../../redux/slices/bulkUserFileNameSlice';
import {
  bulkUploadUsersTable,
  saveBulkUploadUsers,
} from '../../api/UserManagementService';
import {
  excludeResolvedBulkUploadErrorUsers,
  getErrorUsersFromInitialUpload,
  hasBulkUploadSummaryData,
  normalizeBulkUploadSummary,
} from '../../utils/bulkUserUploadUtlis';
import {openBulkUserRowForEdit} from './bulkUserRowEditUtils';
import AurPopup from '../common/aur-popup/AurPopup';
import {setAddUserModal} from '../../redux/slices/unSaveChangesModalSlice';
import {UserDetail} from './models';
import {useNavigate} from 'react-router-dom';
import AurLoadingIndicator from '../common/aur-loading-indicator/AurLoadingIndictor';
import {
  TABLE_DEFAULT_PAGE_NUMBER,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../constants/constants';
import {useAnalytics} from '../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../constants/analyticsConstants';

const BulkUserResultScreenTable = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentPage, setCurrentPage] = useState<number>(
    TABLE_DEFAULT_PAGE_NUMBER
  );
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();
  const addUserModal = useSelector(
    (state: RootState) => state.unsavedChangesModal.showAddUserModal
  );
  const onSetSortModel = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const userImportTempId = useSelector(
    (state: RootState) => state.bulkUserFile.fileUploadBatchId
  );
  const initialUploadPage = useSelector(
    (state: RootState) => state.bulkUserFile.initialUploadPage
  );
  const consumedInitialUploadAllRef = useRef(false);
  const consumedInitialUploadErrorsRef = useRef(false);
  const skipInitialFetchRef = useRef(false);
  const skipInitialErrorsFetchRef = useRef(false);
  const skipUsersDispatchRef = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const prevCachedUsersLengthRef = useRef<number | null>(null);
  const isErrorsListTapped = useRef(false);

  const maybeClearInitialUploadPage = () => {
    if (
      consumedInitialUploadAllRef.current &&
      consumedInitialUploadErrorsRef.current
    ) {
      dispatch(clearInitialUploadPage());
    }
  };

  const triggerAPI = useSelector(
    (state: RootState) => state.userResultScreen.triggerUserDataAPI
  );
  const cachedUsers = useSelector(
    (state: RootState) => state.userResultScreen.users
  );

  const [usersData, setUsersData] = useState<UserDetail[]>([]);

  // used enum for filtering user data
  const [filterType, setFilterType] = useState<UserRecordsFilter>(
    UserRecordsFilter.ALL
  );

  // State to manage the visibility of the dismissable banner
  const [showBanner, setShowBanner] = useState(true);
  const [page, setPage] = useState(TABLE_DEFAULT_PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(TABLE_PER_PAGE_DEFAULT_NO_ROW);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [fixErrorFetchData, setFixErrorFetchData] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const bulkUserFileName: string = useSelector(
    (state: RootState) => state.bulkUserFile.fileName
  );
  const {
    fixErrorTab,
    currentUser,
    value,
    summary: summaryRecord,
  } = useSelector((state: RootState) => state.bulkUserEdit);

  const lastDotIndex = bulkUserFileName.lastIndexOf('.');
  const navigate = useNavigate();

  useEffect(() => {
    if (skipUsersDispatchRef.current) {
      skipUsersDispatchRef.current = false;
      return;
    }
    dispatch(setUsers(usersData));
  }, [usersData]);

  useEffect(() => {
    // Do not overwrite pending POST hydration with stale Redux cache
    if (
      initialUploadPage &&
      !consumedInitialUploadAllRef.current &&
      !consumedInitialUploadErrorsRef.current
    ) {
      return;
    }

    if (cachedUsers == null) {
      return;
    }

    const prevLength = prevCachedUsersLengthRef.current;
    const nextLength = cachedUsers.length;

    // Ignore stale empty redux cache on initial load / before first population
    if (nextLength === 0 && (prevLength === null || prevLength === 0)) {
      prevCachedUsersLengthRef.current = nextLength;
      return;
    }

    prevCachedUsersLengthRef.current = nextLength;
    skipUsersDispatchRef.current = true;
    setUsersData(cachedUsers);
  }, [cachedUsers, initialUploadPage]);

  useEffect(() => {
    if (!fixErrorTab) {
      return;
    }

    setFilterType(UserRecordsFilter.USER_RECORDS_WITH_ERROR);
    dispatch(setValue(1));
    setPage(TABLE_DEFAULT_PAGE_NUMBER);
    setCurrentPage(TABLE_DEFAULT_PAGE_NUMBER);

    if (
      initialUploadPage?.users?.length &&
      !consumedInitialUploadErrorsRef.current
    ) {
      const errorUsers = excludeResolvedBulkUploadErrorUsers(
        getErrorUsersFromInitialUpload(
          initialUploadPage.users as Array<{
            isError?: boolean;
            errors?: unknown[];
            userImportTempId?: unknown;
          }>
        ),
        (cachedUsers ?? []) as Array<{
          isError?: boolean;
          errors?: unknown[];
          userImportTempId?: unknown;
        }>
      );

      if (errorUsers.length > 0) {
        consumedInitialUploadErrorsRef.current = true;
        skipInitialErrorsFetchRef.current = true;
        skipUsersDispatchRef.current = true;
        dispatch(setUsers(errorUsers as UserDetail[]));
        setUsersData(errorUsers as UserDetail[]);
        if (
          initialUploadPage.summary &&
          !hasBulkUploadSummaryData(summaryRecord)
        ) {
          dispatch(setSummary(initialUploadPage.summary));
        }
        setTotalCount(
          summaryRecord?.totalErrorUsers ??
            initialUploadPage.summary?.totalErrorUsers ??
            errorUsers.length
        );
        dispatch(
          setCurrentRow({
            currentRow: 1,
            totalRow: errorUsers.length,
            startRow: 1,
            page: TABLE_DEFAULT_PAGE_NUMBER,
            pageSize,
          })
        );
        dispatch(setFixErrorTab(false));
        dispatch(setTab(UserRecordsFilter.USER_RECORDS_WITH_ERROR));
        openBulkUserRowForEdit(
          errorUsers[0],
          UserRecordsFilter.USER_RECORDS_WITH_ERROR,
          dispatch,
          {
            setDrawer,
            setErrors,
            setBulkUserEdit,
            setTab,
            setDismissbanner,
          }
        );
        maybeClearInitialUploadPage();
        return;
      }
    }

    setFixErrorFetchData(true);
  }, [
    fixErrorTab,
    initialUploadPage,
    dispatch,
    pageSize,
    cachedUsers,
    summaryRecord,
  ]);

  useEffect(() => {
    if (value === 1 || value == 2) {
      setShowBanner(true);
    } else {
      setShowBanner(false);
    }
  }, [value]);

  useEffect(() => {
    setCurrentPage(TABLE_DEFAULT_PAGE_NUMBER);
    setPage(TABLE_DEFAULT_PAGE_NUMBER);
    skipInitialFetchRef.current = false;
    if (value === 0) {
      setFilterType(UserRecordsFilter.ALL);
    } else if (value === 1) {
      setFilterType(UserRecordsFilter.USER_RECORDS_WITH_ERROR);
    } else if (value === 2) {
      setFilterType(UserRecordsFilter.USER_RECORDS_WITH_NO_ERROR);
    }
  }, [value]);

  const isDefaultListQuery =
    page === TABLE_DEFAULT_PAGE_NUMBER &&
    pageSize === TABLE_PER_PAGE_DEFAULT_NO_ROW &&
    filterType === UserRecordsFilter.ALL &&
    (!sortModel?.length || !sortModel[0]?.field) &&
    !triggerAPI &&
    !fixErrorFetchData;

  const isDefaultErrorsListQuery =
    page === TABLE_DEFAULT_PAGE_NUMBER &&
    pageSize === TABLE_PER_PAGE_DEFAULT_NO_ROW &&
    filterType === UserRecordsFilter.USER_RECORDS_WITH_ERROR &&
    (!sortModel?.length || !sortModel[0]?.field) &&
    !triggerAPI;

  useEffect(() => {
    if (
      !initialUploadPage ||
      !userImportTempId ||
      !isDefaultListQuery ||
      consumedInitialUploadAllRef.current
    ) {
      return;
    }

    consumedInitialUploadAllRef.current = true;
    skipInitialFetchRef.current = true;
    fetchRequestIdRef.current += 1;
    setUsersData(initialUploadPage.users ?? []);
    if (initialUploadPage.summary) {
      dispatch(setSummary(initialUploadPage.summary));
    }
    if (value === 0) {
      setTotalCount(initialUploadPage.summary?.totalUsers ?? 0);
    }
    maybeClearInitialUploadPage();
    dispatch(setTriggerUserDataAPI(false));
  }, [
    initialUploadPage,
    userImportTempId,
    isDefaultListQuery,
    value,
    dispatch,
  ]);

  useEffect(() => {
    if (!userImportTempId) {
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      if (
        isDefaultListQuery &&
        initialUploadPage &&
        !consumedInitialUploadAllRef.current
      ) {
        return;
      }

      if (skipInitialFetchRef.current && isDefaultListQuery) {
        skipInitialFetchRef.current = false;
        return;
      }

      if (skipInitialErrorsFetchRef.current && isDefaultErrorsListQuery) {
        skipInitialErrorsFetchRef.current = false;
        return;
      }

      if (
        isDefaultErrorsListQuery &&
        !isErrorsListTapped.current &&
        initialUploadPage &&
        !consumedInitialUploadErrorsRef.current &&
        getErrorUsersFromInitialUpload(
          initialUploadPage.users as Array<{
            isError?: boolean;
            errors?: unknown[];
          }>
        ).length > 0
      ) {
        return;
      }
      isErrorsListTapped.current = false;

      const requestId = ++fetchRequestIdRef.current;
      setIsTableLoading(true);

      const filterData = {
        SearchTerm: filterType,
        PageNumber: page,
        pageSize: pageSize,
        SortColumn: sortModel[0]?.field,
        SortOrder: sortModel[0]?.sort === 'asc' ? 'Asc' : 'Desc',
      };

      // Set default SortOrder to 'Asc' if sortModel is empty
      if (!sortModel?.length || !sortModel[0]?.field) {
        filterData.SortOrder = 'Asc';
      }

      try {
        const response = await bulkUploadUsersTable(
          userImportTempId,
          filterData
        );
        if (cancelled || requestId !== fetchRequestIdRef.current) {
          return;
        }

        const users = response.data?.data?.users ?? [];
        const summary = normalizeBulkUploadSummary(
          response.data?.data?.summary
        );
        setUsersData(users);
        if (hasBulkUploadSummaryData(summary)) {
          dispatch(setSummary(summary));
        }
        if (users.length === 0 && page > 1) {
          dispatch(
            setCurrentRow({
              page: page - 1,
              pageSize: pageSize,
              currentRow: ((page - 1) * pageSize) as number,
              totalRow: currentUser.totalRow - 1,
              startRow: (page - 2) * pageSize + 1,
            })
          );
          setCurrentPage(currentPage - 1);
          setPage(page - 1);
        }
        if (fixErrorTab && filterType === 'Errors') {
          dispatch(
            setCurrentRow({
              currentRow: 1,
              totalRow: users.length,
              startRow: 1,
              page: 1,
              pageSize: pageSize,
            })
          );
          dispatch(setFixErrorTab(false));
          dispatch(setTab(filterType));
          if (users.length > 0) {
            openBulkUserRowForEdit(users[0], filterType, dispatch, {
              setDrawer,
              setErrors,
              setBulkUserEdit,
              setTab,
              setDismissbanner,
            });
          }
        }
        if (hasBulkUploadSummaryData(summary)) {
          if (value === 0) {
            setTotalCount(summary.totalUsers ?? 0);
          } else if (value === 1) {
            setTotalCount(summary.totalErrorUsers ?? 0);
          } else {
            setTotalCount(summary.totalExistingUser ?? 0);
          }
        }
      } catch (error) {
        console.log('res error', error);
      } finally {
        if (!cancelled) {
          dispatch(setTriggerUserDataAPI(false));
          setFixErrorFetchData(false);
          setIsTableLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    filterType,
    page,
    pageSize,
    sortModel,
    triggerAPI,
    fixErrorFetchData,
    userImportTempId,
    isDefaultListQuery,
  ]);

  const totalUserCount = summaryRecord?.totalUsers ?? 0;
  const totalSuccessCount = summaryRecord?.totalSuccessUsers ?? 0;
  const errorCount = summaryRecord?.totalErrorUsers ?? 0;

  useEffect(() => {
    if (value === 0) {
      setTotalCount(totalUserCount);
    } else if (value === 1) {
      setTotalCount(errorCount);
    } else {
      setTotalCount(summaryRecord?.totalExistingUser ?? 0);
    }
  }, [value, totalUserCount, errorCount, summaryRecord?.totalExistingUser]);

  const handleDismiss = () => {
    setShowBanner(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRowClick = (rowData: any) => {
    openBulkUserRowForEdit(rowData, filterType, dispatch, {
      setDrawer,
      setErrors,
      setBulkUserEdit,
      setTab,
      setDismissbanner,
    });
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentPage(TABLE_DEFAULT_PAGE_NUMBER);
    setPage(TABLE_DEFAULT_PAGE_NUMBER);
    dispatch(setValue(newValue));
    skipInitialFetchRef.current = false;
    skipInitialErrorsFetchRef.current = false;
    if (newValue === 0) {
      setFilterType(UserRecordsFilter.ALL);
    } else if (newValue === 1) {
      isErrorsListTapped.current = true;
      setFilterType(UserRecordsFilter.USER_RECORDS_WITH_ERROR);
    } else if (newValue === 2) {
      setFilterType(UserRecordsFilter.USER_RECORDS_WITH_NO_ERROR);
    }
  };

  const handleRowSelectionChange = (newSelection: GridRowSelectionModel) => {
    const rowData = usersData;
    const index = rowData.findIndex(
      (row) => row.userImportTempId === newSelection[0]
    );
    dispatch(setUsers(rowData));
    dispatch(
      setCurrentRow({
        currentRow: ((page - 1) * pageSize + index + 1) as number,
        totalRow: (page - 1) * pageSize + rowData.length,
        startRow: (page - 1) * pageSize + 1,
        page: page,
        pageSize: pageSize,
      })
    );
  };

  const addUserHandler = () => {
    setShouldFetchData(true);
    dispatch(setAddUserModal(false));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.yesAddTeamMembers'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.users.addTeamMemberAction', {
          successCount: totalSuccessCount,
          totalCount: Number(totalSuccessCount) + Number(errorCount),
        }),
        details:
          errorCount === 0
            ? t('gaTracking.users.successful')
            : t('gaTracking.users.partialSuccessful'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.addSubpage', {
          successCount: totalSuccessCount,
          totalCount: Number(totalSuccessCount) + Number(errorCount),
        }),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };
  const [isFetchDataLoading, setIsFetchDataLoading] = useState(false);

  // Reset the state after fetching
  useEffect(() => {
    const fetchData = async () => {
      setIsFetchDataLoading(true);
      try {
        await saveBulkUploadUsers(userImportTempId);
        navigate('/users');
        dispatch(setShowToastIn(true));
        // Reset the state after fetching
      } catch (error) {
        console.error('Error saving user:', error);
      } finally {
        setShouldFetchData(false);
        setIsFetchDataLoading(false);
      }
    };

    if (shouldFetchData) {
      fetchData();
    }
  }, [shouldFetchData]);

  const closeModalHandler = () => {
    dispatch(setAddUserModal(false));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.users.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.users.addTeamMemberCancelAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.users.addSubpage', {
          successCount: totalSuccessCount,
          totalCount: Number(totalSuccessCount) + Number(errorCount),
        }),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  return (
    <div className="bulk-user-result-screen-table">
      <Typography
        className="typo-text headline3_size file-name "
        data-testid="file-name"
      >
        {`${bulkUserFileName.slice(0, lastDotIndex)}.${bulkUserFileName.slice(lastDotIndex + 1).toUpperCase()}`}
      </Typography>
      <div>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{'aria-labelledby': 'basic-button'}}
          disableScrollLock
        >
          <MenuItem onClick={handleClose}>
            {LocaleStrings.userHomePage.newTeamMember}
          </MenuItem>
          <MenuItem onClick={handleClose}>
            {LocaleStrings.userHomePage.grpTeamMembers}
          </MenuItem>
        </Menu>
      </div>

      <Box className="bulk-user-result-screen-table_container">
        <Box className="bulk-user-result-screen-table_tabs">
          <Tabs
            value={value}
            onChange={handleTabChange}
            aria-label={t('common.dismissBanner.bulkUserResultTabs')}
          >
            <Tab
              sx={{textTransform: 'none'}}
              label={`${t('bulkUserResultScreenTable.tabAll')} (${summaryRecord?.totalUsers})`}
              className="typo-text label1_size"
              data-testid="all"
            />
            <Tab
              sx={{textTransform: 'none'}}
              className="typo-text body3_size"
              label={`${t('bulkUserResultScreenTable.tabRowWithErrors')} (${summaryRecord?.totalErrorUsers})`}
              data-testid="rows with error"
            />
            <Tab
              sx={{textTransform: 'none'}}
              className="typo-text body3_size"
              label={`${t('bulkUserResultScreenTable.tabExistingTeamMembers')} (${summaryRecord?.totalExistingUser})`}
              data-testid="existing team member"
            />
          </Tabs>
        </Box>
        {/* Conditionally show the banner only on the 'Existing Team Members' tab */}
        {showBanner ? (
          <AurDismissBanner
            className="aur-dismiss-banner-container"
            bannerHeading={
              value === 2
                ? t('common.dismissBanner.title')
                : t('common.dismissBanner.errorTitle')
            }
            bannerSummary={
              value === 2
                ? t('common.dismissBanner.summary')
                : t('common.dismissBanner.errorSummary')
            }
            icon={<AurSvgIcon src="images/dismiss_icon.svg" />}
            aria-live="assertive"
          >
            <AurButton
              classNames="dismiss-button"
              type={'exit'}
              label={t('buttons.dismiss')}
              onClick={handleDismiss}
              aria-label={t('common.dismissBanner.dismissBannerBtnAriaLabel')}
              size="Medium"
              dataTestId="dismiss button"
            />
          </AurDismissBanner>
        ) : null}

        <AurDataGrid
          rows={usersData}
          columns={BulkUserResultTableColumns()}
          getRowId={(row) => row.userImportTempId}
          pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
          checkBoxVisible={false}
          onRowClick={(params) => handleRowClick(params.row)}
          getRowClassName={(params) =>
            params?.row?.isError ? 'error-row' : 'custom-row-class'
          }
          visibleToolTip={true}
          entityType={t('bulkUserResultScreenTable.userEntity')}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          onRowSelectionModelChange={handleRowSelectionChange}
          onPageChange={(page: number) => setPage(page)}
          onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
          sortingMode="server"
          paginationMode="server"
          sortModel={sortModel}
          setSortModel={onSetSortModel}
          totalRowCount={totalCount}
        />

        {addUserModal && (
          <div data-testid="add-user-popup">
            <AurPopup
              className="add-user-popup"
              open={addUserModal}
              onOk={addUserHandler}
              onCancel={closeModalHandler}
              popupTitle={t('common.addUserModal.title')}
              popupHeading={t('common.addUserModal.headingMsg', {
                successCount: totalSuccessCount,
                totalCount: totalUserCount,
              })}
              showPopupHeading={true}
              showPopupTitle={true}
              showCloseIcon={true}
              popupContent={t('common.addUserModal.content')}
              labelActionCancel={t('common.addUserModal.cancelBtn')}
              labelActionOk={t('common.addUserModal.addTeamMemberBtn')}
              closeIconHint={t('common.addUserModal.closeIconHint')}
            />
          </div>
        )}
      </Box>
      {
        <AurLoadingIndicator
          overlay={true}
          isOpen={isFetchDataLoading || isTableLoading}
        />
      }
    </div>
  );
};

export default BulkUserResultScreenTable;
